import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const TeacherDetail = () => {
  const { teacherId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTeacher();
  }, [teacherId]);

  const fetchTeacher = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/teacher/${teacherId}`,
        {
          headers: {
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch teacher');
      }

      const data = await response.json();
      setTeacher(data);
    } catch (err) {
      console.error('Error fetching teacher:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeacher = async () => {
    try {
      // Get current user profile
      const profileResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/profile/${currentUser.uid}`,
        {
          headers: {
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          }
        }
      );

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch profile');
      }

      const profileData = await profileResponse.json();

      // Update profile with selected teacher
      const updateResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/profile/${currentUser.uid}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({
            ...profileData,
            teacherId: teacher.id
          })
        }
      );

      if (!updateResponse.ok) {
        throw new Error('Failed to update teacher');
      }

      // Navigate to chat
      navigate(`/chat/${currentUser.uid}`);
    } catch (err) {
      console.error('Error selecting teacher:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-red-600 text-xl mb-4">{error || 'Teacher not found'}</p>
            <button
              onClick={() => navigate('/teachers')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Teachers
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
        <button
          onClick={() => navigate('/teachers')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Teachers
        </button>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Teacher Image */}
              <div className="flex-shrink-0">
                {teacher.picture ? (
                  <img
                    src={teacher.picture}
                    alt={teacher.name}
                    className="h-48 w-48 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-48 w-48 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-6xl font-bold text-white">
                      {teacher.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Teacher Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{teacher.name}</h1>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    teacher.type === 'PREMIUM'
                      ? 'bg-purple-100 text-purple-800'
                      : teacher.type === 'CUSTOM'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {teacher.type}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    {teacher.level}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {teacher.specialty}
                  </span>
                  {teacher.gender && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {teacher.gender}
                    </span>
                  )}
                  {teacher.age && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      Age {teacher.age}
                    </span>
                  )}
                </div>

                {teacher.biography && (
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Biography</h2>
                    <p className="text-gray-700">{teacher.biography}</p>
                  </div>
                )}

                {teacher.personality && (
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Personality</h2>
                    <p className="text-gray-700">{teacher.personality}</p>
                  </div>
                )}

                {teacher.teachingStyle && (
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Teaching Style</h2>
                    <p className="text-gray-700">{teacher.teachingStyle}</p>
                  </div>
                )}

                {teacher.audio_preview && (
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Voice Preview</h2>
                    <audio controls className="w-full">
                      <source src={teacher.audio_preview} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={handleSelectTeacher}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium text-lg"
                  >
                    Select This Teacher
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TeacherDetail;

